<template>
  <v-app v-if="!isEmbed">
    <!-- Skip link -->
    <a
      href="#main-content"
      class="skip-link"
    >
      {{ t("general.saltarContenido") }}
    </a>

    <v-app-bar
      tag="div"
      role="banner"
      color="white"
      density="default"
      class="app-bar-custom"
      elevation="1"
    >
      <v-container
        fluid
        class="responsive-container"
      >
        <div class="d-flex align-center justify-space-between header-content">
          <!-- Izquierda: botón menú móvil + logo -->
          <div class="d-flex align-center flex-shrink-1">
            <v-app-bar-nav-icon
              class="d-lg-none menu-icon-mobile"
              :aria-label="t('general.abrirMenu')"
              :aria-expanded="appStore.drawer"
              @click="appStore.drawer = !appStore.drawer"
            />

            <router-link
              :to="localePath('/')"
              :aria-label="t('general.irPaginaPrincipal')"
              class="logo-link"
            >
              <img
                src="@/assets/CNMC_Logotipo.png"
                alt="Logotipo CNMC - Test de Velocidad"
                class="logo-img"
                width="200"
                height="36"
                fetchpriority="high"
              >
            </router-link>
          </div>

          <!-- Derecha: selector de idioma -->
          <div class="language-selector-wrapper">
            <v-btn
              ref="langBtnRef"
              variant="text"
              class="language-button"
              :aria-label="t('general.seleccionarIdioma')"
              :aria-expanded="languageMenuOpen"
              @click.stop="languageMenuOpen = !languageMenuOpen"
            >
              <img
                :src="currentLanguage.bandera"
                :alt="`Bandera de ${currentLanguage.title}`"
                class="flag-icon-btn"
                width="22"
                height="16"
                aria-hidden="true"
              >
              <span class="language-label d-none d-sm-inline">{{
                currentLanguage.title
              }}</span>
              <v-icon
                class="chevron-icon"
                size="small"
              >
                mdi-chevron-down
              </v-icon>
            </v-btn>

            <Teleport to="body">
              <div
                v-if="languageMenuOpen"
                aria-hidden="true"
                class="language-overlay"
                @click="languageMenuOpen = false"
              />
              <v-card
                v-if="languageMenuOpen"
                class="language-dropdown"
                elevation="5"
                :style="dropdownStyle"
              >
                <v-list density="compact">
                  <v-list-item
                    v-for="lang in languages"
                    :key="lang.value"
                    :active="currentLocale === lang.value"
                    class="language-menu-item"
                    @click="switchLanguage(lang.value); languageMenuOpen = false"
                  >
                    <template #prepend>
                      <img
                        :src="lang.bandera"
                        :alt="`Bandera de ${lang.title}`"
                        class="flag-icon-menu"
                        aria-hidden="true"
                      >
                    </template>
                    <v-list-item-title class="language-menu-text">
                      {{ lang.title }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </Teleport>
          </div>
        </div>
      </v-container>
    </v-app-bar>

    <!-- Main content -->
    <v-main
      id="main-content"
      role="main"
      class="custom-main"
    >
      <Menu />
      <v-container class="responsive-container main-container">
        <router-view v-slot="{ Component }">
          <Transition
            name="page"
            mode="out-in"
          >
            <component :is="Component" />
          </Transition>
        </router-view>
      </v-container>
    </v-main>

    <ScrollToTop />

    <!-- Footer -->
    <v-footer
      class="footer-simple"
      role="contentinfo"
    >
      <v-container class="responsive-container">
        <!-- Línea 1: Enlaces -->
        <nav
          class="footer-links"
          :aria-label="t('general.enlacesPie')"
        >
          <router-link
            :to="localePath('/informacion-legal#aviso-legal-y-terminos-de-uso')"
            class="footer-link"
          >
            {{ t("footer.avisoLegal") }}
          </router-link>
          <router-link
            :to="localePath('/informacion-legal#politica-privacidad')"
            class="footer-link"
          >
            {{ t("footer.politicaPrivacidad") }}
          </router-link>
          <router-link
            :to="localePath('/politica-cookies')"
            class="footer-link"
          >
            {{ t("footer.politicaCookies") }}
          </router-link>
          <router-link
            :to="localePath('/preguntas-frecuentes')"
            class="footer-link"
          >
            {{ t("footer.faq") }}
          </router-link>
          <router-link
            :to="localePath('/accesibilidad')"
            class="footer-link"
          >
            {{ t("footer.accesibilidad") }}
          </router-link>
          <router-link
            :to="localePath('/mapa-sitio')"
            class="footer-link"
          >
            {{ t("footer.mapaSitio") }}
          </router-link>
          <button
            class="footer-link footer-btn"
            @click="openCookieSettings"
          >
            {{ t("footer.configurarCookies") }}
          </button>
        </nav>

        <!-- Línea 2: Copyright -->
        <div class="footer-bottom">
          <span class="footer-copyright">{{ t("footer.copyright") }}</span>
          <span class="footer-separator">•</span>
          <a
            href="https://www.cnmc.es/"
            target="_blank"
            rel="noopener noreferrer"
            class="footer-link"
          >
            CNMC.es
          </a>
        </div>
      </v-container>
    </v-footer>

    <CookieBanner />
  </v-app>

  <div v-else>
    <router-view />
  </div>
</template>

<script setup>
import "@/assets/styles/menuStyles.css";
import "flag-icons/css/flag-icons.min.css";
import Menu from "@/components/Menu/Menu";
import CookieBanner from "@/components/CookieBanner/CookieBanner.vue";
import ScrollToTop from "@/components/ScrollToTop.vue";
import { useAppStore } from "@/stores/app.store";
import { useCookieConsent } from "@/composables/useCookieConsent";
import { useSeoHead } from "@/composables/useSeoHead";
import { onBeforeMount, onMounted, watch, nextTick, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { getLocaleFromRoute, navigateToLocale } from "@/router";
import banderaEs from "@/assets/banderas/es.svg";
import banderaEn from "@/assets/banderas/en.svg";

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const { t, locale } = useI18n({ useScope: "global" });

// Estado del menú de idioma
const languageMenuOpen = ref(false);
const langBtnRef = ref(null);

// Posición dinámica del dropdown (Teleport al body)
const dropdownStyle = computed(() => {
  const btn = langBtnRef.value?.$el || langBtnRef.value;
  if (!btn) return {};
  const rect = btn.getBoundingClientRect();
  return {
    position: 'fixed',
    top: `${rect.bottom + 4}px`,
    right: `${window.innerWidth - rect.right}px`,
    zIndex: 100001,
  };
});
const { initCookieConsent, showConfigModal } = useCookieConsent();
useSeoHead();

const languages = [
  {
    title: "Español",
    value: "es",
    bandera: banderaEs,
  },
  {
    title: "English",
    value: "en",
    bandera: banderaEn,
  },
];

// Locale actual basado en la URL
const currentLocale = computed(() => getLocaleFromRoute(route));

// Idioma actual para mostrar en el selector
const currentLanguage = computed(() => {
  return (
    languages.find((lang) => lang.value === currentLocale.value) || languages[0]
  );
});

// Helper para generar rutas localizadas
const localePath = (path) => {
  if (currentLocale.value === "en") {
    // Separar path y hash
    const [basePath, hash] = path.split("#");
    const localizedPath = `/en${basePath === "/" ? "" : basePath}`;
    return hash ? `${localizedPath}#${hash}` : localizedPath;
  }
  return path;
};

// Cambiar idioma navegando a la ruta equivalente
const switchLanguage = (targetLocale) => {
  navigateToLocale(router, route, targetLocale);
};

// Rutas embed (sin header/footer)
const isEmbed = computed(() => {
  if (route.query.embed === "true" || route.query.embedded === "1") return true;
  const embedRoutes = ["testEmbed"];
  if (embedRoutes.includes(route.name?.replace(/-en$/, ""))) return true;
  if (route.path.includes("test-embed")) return true;
  return false;
});

// Sincronizar vue-i18n con el locale de la URL
watch(
  currentLocale,
  async (newLocale) => {
    if (locale.value !== newLocale) {
      locale.value = newLocale;
    }
    document.documentElement.lang = newLocale;
    // Las rutas ocultas/embebidas (isHidden) nunca muestran el aviso legal
    if (!route.meta?.isHidden) {
      await appStore.fetchPageContent(newLocale);
    }
  },
  { immediate: true }
);

onBeforeMount(() => {
  // Establecer idioma inicial basado en la URL
  const urlLocale = getLocaleFromRoute(route);
  locale.value = urlLocale;
  // fetchPageContent ya se llama en el watch(currentLocale, { immediate: true }) de arriba,
  // no hace falta duplicarlo aquí
});

onMounted(() => {
  initCookieConsent();
});

const openCookieSettings = () => {
  showConfigModal.value = true;
};

watch(
  () => route.hash,
  async (hash) => {
    if (!hash) return;
    await nextTick();
    const el = document.querySelector(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      el.setAttribute("tabindex", "-1");
      el.focus();
    }
  }
);
</script>

<style scoped>
/* ===== SKIP LINK ===== */
.skip-link {
  position: absolute;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 0.75rem 1.5rem;
  background-color: rgb(var(--v-theme-primary));
  color: white;
  text-decoration: none;
  border-radius: 0 0 8px 8px;
  font-weight: 500;
  transition: top var(--transition-fast);
}

.skip-link:focus {
  top: 0;
  outline: 3px solid rgb(var(--v-theme-primary));
}

/* ===== APP BAR ===== */
.app-bar-custom {
  padding: 0 20px;
  background: linear-gradient(180deg, #ffffff 0%, #fafbfc 100%) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03) !important;
  z-index: 1001 !important;
}

.app-bar-custom :deep(.v-toolbar__content) {
  overflow: visible !important;
}

.app-bar-custom :deep(.responsive-container) {
  pointer-events: none;
}

.app-bar-custom :deep(.responsive-container *) {
  pointer-events: auto;
}

.header-content {
  width: 100%;
  overflow: visible;
  /* Más altura y mejor espaciado */
  padding: clamp(10px, 1.2vw + 6px, 16px) 0;
  gap: clamp(8px, 1.5vw + 4px, 20px);
}

/* Logo */
.logo-link {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: opacity var(--transition-fast);
  margin-left: 8px;
}

.logo-link:hover {
  opacity: 0.8;
}

.logo-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 4px;
  border-radius: var(--radius-sm);
}

.logo-img {
  height: 36px;
  width: auto;
  max-width: 240px;
  object-fit: contain;
  display: block;
}

.menu-icon-mobile {
  margin-right: 8px;
  flex-shrink: 0;
  align-self: center;
}

.menu-icon-mobile :deep(.v-btn__content) {
  line-height: 1;
}

/* ===== SELECTOR DE IDIOMA ===== */
.language-button {
  min-width: auto !important;
  height: 40px !important;
  background-color: transparent !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  padding: 0 12px !important;
  border-radius: 8px !important;
  flex-shrink: 0;
}

.language-button :deep(.v-btn__content) {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 500 !important;
  gap: 8px;
}

.language-button:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}

.language-button:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary)) !important;
  outline-offset: 2px !important;
}

.flag-icon-btn {
  width: 22px;
  height: 16px;
  object-fit: cover;
  border-radius: var(--radius-sm);
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.language-label {
  color: rgba(0, 0, 0, 0.87) !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  white-space: nowrap;
}

.chevron-icon {
  color: rgba(0, 0, 0, 0.5) !important;
  margin-left: 0 !important;
  flex-shrink: 0;
}

/* Wrapper del selector de idioma */
.language-selector-wrapper {
  position: relative;
}

/* Overlay transparente para cerrar al hacer click fuera */
.language-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
}

/* Dropdown del menú de idioma (Teleport al body) */
.language-dropdown {
  min-width: 160px;
  border-radius: 12px !important;
}

.language-menu-item {
  min-height: 48px;
}

.language-menu-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.language-menu-item.v-list-item--active {
  background-color: rgba(var(--v-theme-primary), 0.12);
}

.flag-icon-menu {
  width: 22px;
  height: 16px;
  object-fit: cover;
  margin-right: 12px;
  border-radius: var(--radius-sm);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.language-menu-text {
  color: rgba(0, 0, 0, 0.87) !important;
  font-weight: 500 !important;
  font-size: 0.9375rem !important;
}

/* ===== LAYOUT STICKY FOOTER ===== */
.v-application :deep(.v-application__wrap) {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ===== MAIN CONTENT ===== */
.custom-main {
  background-color: var(--color-background-subtle);
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.v-footer {
  flex-shrink: 0;
}

/* Contenedor responsive - máximo aprovechamiento del ancho */
.responsive-container {
  max-width: 1400px;
  margin: 0 auto;
  padding-left: 16px;
  padding-right: 16px;
}

/* Pantallas pequeñas */
@media (max-width: 768px) {
  .responsive-container {
    max-width: 100%;
    padding-left: 12px;
    padding-right: 12px;
  }
}

/* Portátiles medianos (1366px - 1600px) */
@media (min-width: 1280px) {
  .responsive-container {
    max-width: 1600px;
  }
}

/* Portátiles grandes / monitores pequeños */
@media (min-width: 1600px) {
  .responsive-container {
    max-width: 1760px;
  }
}

/* Monitores grandes */
@media (min-width: 1920px) {
  .responsive-container {
    max-width: 1920px;
  }
}

/* Pantallas muy grandes */
@media (min-width: 2560px) {
  .responsive-container {
    max-width: 2400px;
  }
}

.main-container {
  /* Espaciado consistente */
  padding-top: 8px !important;
  padding-bottom: 0 !important;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ===== FOOTER ===== */
.footer-simple {
  background: rgb(var(--v-theme-primary));
  padding: 10px 0 !important;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 2px 16px;
  margin-bottom: 4px;
}

.footer-link {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  transition: color var(--transition-fast);
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  padding: 6px 4px;
  min-height: 44px;
}

.footer-link:hover {
  color: #fff;
}

.footer-link:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

.footer-btn {
  background: none;
  border: none;
  padding: 6px 4px;
  margin: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  min-height: 44px;
}

.footer-bottom {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
}

.footer-copyright {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
}

.footer-separator {
  color: rgba(255, 255, 255, 0.4);
}

/* ===== RESPONSIVE ===== */

/* Tablets */
@media (max-width: 960px) {
  .app-bar-custom {
    padding: 0 12px;
  }

  .main-container {
    padding-top: 12px !important;
    padding-bottom: 8px !important;
  }
}

/* Móviles */
@media (max-width: 600px) {
  /* Footer al final natural del scroll: el main ocupa al menos el viewport
     restante para que el footer no asome en la parte inferior cuando el
     contenido es corto. Solo se ve al hacer scroll completo. */
  .v-application :deep(.v-application__wrap) {
    min-height: auto;
  }

  .custom-main {
    min-height: calc(100dvh - 56px);
  }

  .app-bar-custom {
    padding: 0 4px;
  }

  .main-container {
    padding-top: 2px !important;
    padding-bottom: 12px !important;
  }

  .menu-icon-mobile {
    margin-right: 2px;
  }

  .language-button {
    height: 36px !important;
    padding: 0 8px !important;
  }

  .flag-icon-btn {
    width: 20px;
    height: 14px;
  }

  .footer-simple {
    height: auto;
    min-height: auto;
    max-height: none;
    padding: 8px 0 !important;
    overflow: visible;
  }

  .footer-links {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 12px;
    margin-bottom: 6px;
    text-align: center;
  }

  .footer-link {
    font-size: 11px;
    padding: 4px;
    min-height: 36px;
    justify-content: center;
  }

  .footer-btn {
    font-size: 11px;
    min-height: 36px;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 2px;
  }

  .footer-copyright {
    font-size: 10px;
    text-align: center;
  }

  .footer-separator {
    display: none;
  }

  .footer-bottom .footer-link {
    font-size: 10px;
  }
}

/* Móviles muy pequeños */
@media (max-width: 360px) {
  /* logo-img usa clamp() - no necesita override */

  .language-button {
    min-height: 44px !important;
    height: 44px !important;
    padding: 0 4px !important;
  }

  .flag-icon-btn {
    width: 18px;
    height: 13px;
  }

  /* Footer: en pantallas muy estrechas, 1 columna para que los enlaces
     no se trunquen ("Configurar cookies", etc.) */
  .footer-links {
    grid-template-columns: 1fr;
    gap: 4px 0;
  }
  .footer-link,
  .footer-btn {
    font-size: 10px;
    min-height: 32px;
  }
}

/* Ajustar padding del main en móvil */
@media (max-width: 959px) {
  .custom-main {
    padding-top: 64px !important;
  }
}

/* Transición entre páginas */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.2s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
}
</style>
